/**
 * ACP over stdio via `@agentclientprotocol/sdk` (`ClientSideConnection` + ndJsonStream).
 *
 * **Used only by agents that speak this wire protocol** — today:
 * `@zed-industries/codex-acp`, `@agentclientprotocol/claude-agent-acp`, and Kiro (`kiro-cli acp`).
 *
 * **Not used by Cursor.** The Cursor CLI uses JSON-RPC line protocol in
 * `cursor/cursor-acp-client.ts` (different initialize/session/prompt shapes and handlers).
 * Bridge hooks (`buildAcpSessionBridgeHooks`) are shared; session/update payloads are
 * normalized here to the flat shape the bridge expects (`sessionUpdate` at top level).
 *
 * **Stderr:** Agents may print human-readable errors on stderr (e.g. Kiro login) while still
 * returning a normal `session/prompt` result on stdout. We tee stderr to the terminal and keep a
 * bounded buffer so failures can be merged into `PromptResult.error` and auth heuristics can run.
 * JSON-RPC errors are also normalized per [ACP / JSON-RPC error handling](https://agentclientprotocol.com/protocol/overview#error-handling).
 *
 * @see https://github.com/agentclientprotocol/typescript-sdk
 */

import { getBridgeRoot } from '../../../../files/cwd/index.js';
import {
  formatJsonRpcStyleError,
  mergeErrorWithStderr,
} from '../agent-stderr-capture.js';
import type { AcpClientHandle, AcpClientOptions } from '../acp-client.js';
import { formatSpawnError } from '../format-spawn-error.js';
import { createSdkStdioHandle } from './create-sdk-stdio-handle.js';
import { createSdkStdioSessionContext } from './create-sdk-stdio-session-context.js';
import { bootstrapSdkStdioConnection } from './sdk-stdio-bootstrap-connection.js';
import { createSdkStdioInitSettle } from './sdk-stdio-init-settle.js';
import { attachSdkStdioStderrAuthWatch } from './sdk-stdio-stderr-auth-watch.js';
import { spawnSdkStdioProcess } from './spawn-sdk-stdio-process.js';
import type { SdkStdioPermissionPendingEntry } from './sdk-stdio-permission-pending.js';

export async function createSdkStdioAcpClient(options: AcpClientOptions): Promise<AcpClientHandle> {
  const { PROTOCOL_VERSION } = await import('@agentclientprotocol/sdk');
  const {
    command,
    cwd = getBridgeRoot(),
    backendAgentType,
    onSessionUpdate,
    onRequest,
    onFileChange,
    killSubprocessAfterCancelMs,
    onAgentSubprocessExit,
    agentConfig,
    persistedAcpSessionId,
    onAcpSessionEstablished,
    onAcpConfigOptionsUpdated,
    getActiveConfigOptions,
  } = options;

  const { child, stderrCapture } = spawnSdkStdioProcess({
    command,
    cwd,
    onAgentSubprocessExit,
  });

  const sessionCtx = createSdkStdioSessionContext({
    cwd,
    mcpServers: options.mcpServers,
    persistedAcpSessionId,
    backendAgentType,
    agentConfig,
    getActiveConfigOptions,
    onAcpSessionEstablished,
    onAcpConfigOptionsUpdated,
    onFileChange,
    stderrCapture,
  });

  return new Promise<AcpClientHandle>((resolve, reject) => {
    const init = createSdkStdioInitSettle(child);

    child.on('error', (err: NodeJS.ErrnoException) => {
      init.settleReject(reject, new Error(formatSpawnError(err, command[0])));
    });

    attachSdkStdioStderrAuthWatch({
      child,
      stderrCapture,
      backendAgentType,
      init,
      reject,
    });

    void (async () => {
      try {
        const pendingPermissionReplies = new Map<string, SdkStdioPermissionPendingEntry>();
        const { transport, established } = await bootstrapSdkStdioConnection({
          child,
          sessionCtx,
          backendAgentType,
          onSessionUpdate,
          onRequest,
          pendingPermissionReplies,
          protocolVersion: PROTOCOL_VERSION,
        });

        init.settleResolve(
          resolve,
          createSdkStdioHandle({
            child,
            sessionId: established.sessionId,
            sessionCtx,
            transport,
            pendingPermissionReplies,
            killSubprocessAfterCancelMs,
          }),
        );
      } catch (err) {
        if (init.settled) return;
        init.settleReject(
          reject,
          new Error(mergeErrorWithStderr(formatJsonRpcStyleError(err), stderrCapture.getText())),
        );
      }
    })();
  });
}
